import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AvForm } from "availity-reactstrap-validation";
import { useSnapshot } from "valtio";

import {
    store as appStore,
    setIsActionsClicked,
    setIsEdited,
    setActions,
} from "../../store";
import { store as userStore } from "@components/VerticalLayout/store";
import { setSelectedRow } from "@pages/SettingGeneral/Language/store";

import { ipMatchWord } from "@constants/index";
import { POS_CODE_CAISSE_REGEX } from "@constants/pos";
import { ConfirmationEditMessage } from "@components/Common/ConfirmationEditMessage";
import ErrorToast from "@components/Common/ErrorTost";
import { useFetchGlobal } from "../../../../../api/hooks";
import { showSuccessToast } from "@components/Common/SuccessToast";
import { UpdateResourceIp } from "@helpers/general";
import { ApplicationHeader } from "@pages/Applications/Sco/ScoDeviceSetup/AddApplication/components/ApplicationHeader";
import { ApplicationForm } from "@pages/Applications/Sco/ScoDeviceSetup/AddApplication/components/ApplicationForm";
import { ApplicationFooter } from "@pages/Applications/Sco/ScoDeviceSetup/AddApplication/components/ApplicationFooter";

import { addApplication, editApplication } from "../api";
import { buildTargetShopIdsSet } from "../helpers";

type applicationConfigurationType = {
    setIsAddApplication: (value: boolean) => void;
    setDataIsUpdated: (value: boolean) => void;
    isAddApplication: boolean;
    isAddNewApplicationButtonClicked: boolean;
    selectedRows: Record<string, any>[];
    data: any;
    editedData: any;
    setEditedData: (value: any) => void;
};

export function AddApplication({
    setIsAddApplication,
    isAddApplication,
    setDataIsUpdated,
    isAddNewApplicationButtonClicked,
    selectedRows,
    data,
    editedData,
    setEditedData,
}: applicationConfigurationType) {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();
    const {
        oneShop,
        userID,
        franchiseID,
        shopID,
        operatorID,
        resourceIp,
    } = useSnapshot(userStore);
    const { isPlusIconClicked, isEdited, isActionsClicked } = useSnapshot(
        appStore
    );

    const [inputDesignation, setInputDesignation] = React.useState<boolean>(
        false
    );
    const [
        isValidateButtonClicked,
        setIsValidateButtonClicked,
    ] = React.useState<boolean>(true);
    const [isModalOpened, setIsModalOpened] = React.useState<boolean>(false);
    const [designation, setDesignation] = useState<string>("");
    const [remark, setRemark] = useState<string>("");
    const [title, setTitle] = useState<string>("");
    const [ip, setIp] = useState<string>("");
    const [isApplicationListExist, setIsApplicationListExist] = React.useState<
        boolean
    >(false);
    const [isIpExist, setIsIpExist] = React.useState<boolean>(false);
    const [codeCaisse, setCodeCaisse] = useState<string>("");
    const [isCodeCaisseRequired, setIsCodeCaisseRequired] = React.useState<
        boolean
    >(false);
    const [isCodeCaisseDuplicate, setIsCodeCaisseDuplicate] = React.useState<
        boolean
    >(false);

    useEffect(() => {
        UpdateResourceIp(userID, franchiseID, fetchGlobal);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const localIps = React.useMemo(() => {
        const currentAppId = selectedRows[0]?.appId;
        // Multi-shop add: aggregate IPs across ALL selected shops so a
        // duplicate IP in any target shop is flagged inline. oneShop keeps
        // its dedicated scope (the user's own shop).
        const targetShopIds = oneShop
            ? new Set<string>([shopID])
            : buildTargetShopIdsSet(selectedRows);
        if (targetShopIds.size === 0) return [];
        return (resourceIp ?? [])
            .filter((entry: any) => {
                const entryId = entry.id ?? entry.Id;
                if (!targetShopIds.has(entry.shopId)) return false;
                if (!entry.ip) return false;
                if (currentAppId !== undefined && entryId === currentAppId)
                    return false;
                return true;
            })
            .map((entry: any) => entry.ip);
    }, [resourceIp, selectedRows, oneShop, shopID]);
    const localNamesApplications = React.useMemo(() => {
        const list = data?.othersData?.listDesignation || [];
        if (list.length === 0) return [];

        const currentAppId = selectedRows[0]?.appId;
        const currentAppName = selectedRows[0]?.[
            t("Application")
        ]?.toUpperCase();

        // Multi-shop add: aggregate the existing-name pool across ALL
        // selected shops so a designation is flagged as duplicate if it
        // already exists in any of the target shops, not just the first
        // row's shop. In edit mode `selectedRows` carries the app row
        // itself (with its own shopId), and in oneShop we ignore the row
        // scope entirely.
        const targetShopIds = buildTargetShopIdsSet(selectedRows);

        const results: string[] = [];

        list.forEach((elt: any) => {
            let matches = false;
            let name = "";

            if (!oneShop) {
                if (targetShopIds.has(elt.shopId)) {
                    name = elt["Application"].toUpperCase();
                    matches = true;
                }
            } else {
                name = elt.toUpperCase();
                matches = true;
            }

            if (matches) {
                if (currentAppId !== undefined) {
                    if (name !== currentAppName) {
                        results.push(name);
                    }
                } else {
                    results.push(name);
                }
            }
        });
        return results;
    }, [data, selectedRows, oneShop, t]);

    const matchWordDesignation = new RegExp(/^[\w]+([-_\s]{1}[A-Za-z0-9]+)*$/i);

    const localCodesCaisse = React.useMemo<string[]>(() => {
        type AppEntry = {
            shopId?: string;
            appId?: string;
            codeCaisse?: string | number;
            subRows?: AppEntry[];
        };
        // Multi-shop add: aggregate codeCaisse across ALL selected shops
        // (same rationale as localNamesApplications / localIps). oneShop
        // pins the scope to the user's own shop.
        const targetShopIds = oneShop
            ? new Set<string>([shopID])
            : buildTargetShopIdsSet(selectedRows);
        const currentAppId = selectedRows[0]?.appId as string | undefined;
        const collected: string[] = [];

        const matchesScope = (entry: AppEntry) => {
            if (!oneShop) {
                if (!entry.shopId || !targetShopIds.has(entry.shopId))
                    return false;
            } else if (entry.shopId && entry.shopId !== shopID) {
                return false;
            }
            if (currentAppId !== undefined && entry.appId === currentAppId)
                return false;
            return true;
        };

        const dedicatedList = data?.othersData?.listCodeCaisse as
            | AppEntry[]
            | undefined;
        if (Array.isArray(dedicatedList)) {
            dedicatedList.forEach((entry) => {
                if (!entry || entry.codeCaisse === undefined) return;
                if (!matchesScope(entry)) return;
                collected.push(String(entry.codeCaisse));
            });
        }

        const rows: AppEntry[] = Array.isArray(data?.data) ? data.data : [];
        rows.forEach((row) => {
            const apps: AppEntry[] = oneShop
                ? [row]
                : Array.isArray(row?.subRows)
                ? row.subRows!
                : [];
            apps.forEach((app) => {
                if (!app || app.codeCaisse === undefined) return;
                const appEntry: AppEntry = {
                    shopId: oneShop ? shopID : app.shopId ?? row?.shopId,
                    appId: app.appId,
                };
                if (!matchesScope(appEntry)) return;
                collected.push(String(app.codeCaisse));
            });
        });

        return Array.from(new Set(collected));
    }, [data, selectedRows, oneShop, shopID]);

    async function handleSaveApplication(isEdit: boolean) {
        setIsValidateButtonClicked(false);

        try {
            // Mirror Kiosk multi-shop add: when the user has selected several
            // shop rows in the table, fan out the same app to each. In edit
            // mode we keep the single-shop path (`selectedRows[0].shopId`)
            // because an app row belongs to exactly one shop.
            const shopIdsFromRows: string[] = selectedRows
                .map((r: any) => r?.shopId)
                .filter(
                    (id: string | undefined): id is string =>
                        typeof id === "string" && id.length > 0
                );

            const commonParams = {
                userID: String(userID),
                franchiseID,
                operatorID,
                oneShop,
                shopID,
                designation,
                ip,
                remark,
                codeCaisse,
            };

            const result: any = isEdit
                ? await editApplication(fetchGlobal, {
                      ...commonParams,
                      shopId: selectedRows[0]?.shopId,
                      appId: selectedRows[0]?.appId,
                      printer: selectedRows[0].printer,
                  })
                : await addApplication(fetchGlobal, {
                      ...commonParams,
                      shopIds: shopIdsFromRows,
                  });

            // Multi-shop add: result carries successShopIds/failedShopIds
            // so we can distinguish total success / partial / total failure.
            // Edit and oneShop both yield N=1 with the same success/error
            // shape so the same branches apply.
            const totalShops: number =
                typeof result?.totalShops === "number" ? result.totalShops : 1;
            const successCount: number = Array.isArray(result?.successShopIds)
                ? result.successShopIds.length
                : result?.error
                ? 0
                : 1;
            const failedCount: number = Array.isArray(result?.failedShopIds)
                ? result.failedShopIds.length
                : result?.error
                ? 1
                : 0;

            if (failedCount > 0 && successCount === 0) {
                throw Error(result?.message || "error");
            }

            if (failedCount > 0 && successCount > 0) {
                // Partial: warn the user with the breakdown so they don't
                // blindly retry and create duplicates on the shops that
                // already succeeded.
                ErrorToast(
                    `${t(
                        "The application was added to {{success}} of {{total}} shops; {{failed}} failed.",
                        {
                            success: successCount,
                            total: totalShops,
                            failed: failedCount,
                        }
                    )!}`
                );
            } else {
                showSuccessToast(
                    `${t(
                        isEdit
                            ? "The application was successfully changed"
                            : "The application was successfully added"
                    )!}`
                );
            }

            setDesignation("");
            setRemark("");
            setIp("");
            setCodeCaisse("");
            setIsCodeCaisseRequired(false);
            setIsCodeCaisseDuplicate(false);
            setDataIsUpdated!(true);
            setIsValidateButtonClicked(true);
            if (isEdit) {
                setIsAddApplication!(!isAddApplication);
                setEditedData({});
            }
            setIsEdited(false);
        } catch (e) {
            ErrorToast(`${t("There's an error")!}`);
        }
    }

    const handleValidate = () => {
        if (designation === "") {
            setInputDesignation(true);
        }

        if (codeCaisse === "") {
            setIsCodeCaisseRequired(true);
        }

        const isValidDesignation =
            designation.length !== 0 &&
            designation.trim() !== "" &&
            designation.match(matchWordDesignation) &&
            !isApplicationListExist;

        const isValidIp = ip === "" || !!ip.match(ipMatchWord);
        const isValidCodeCaisse =
            POS_CODE_CAISSE_REGEX.test(codeCaisse) && !isCodeCaisseDuplicate;

        const isFormValid =
            isValidDesignation && isValidIp && !isIpExist && isValidCodeCaisse;

        // Bail out before the "no changes" modal when any required field is
        // empty or invalid — the inline errors already tell the user what is
        // wrong, and the unchanged-modal would be misleading in that state.
        if (!isFormValid) return;

        const isUnchanged =
            Object.keys(editedData).length !== 0 &&
            editedData[t("Application")] === designation &&
            editedData[t("Note")] === remark &&
            editedData[t("Address IP")] === ip &&
            (editedData.codeCaisse ?? "") === codeCaisse;

        if (isUnchanged) {
            setIsModalOpened(true);
            return;
        }

        if (!oneShop) {
            if (selectedRows.length === 0) {
                ErrorToast(`${t("Attention ! you must select a store.")!}`);
            } else if (selectedRows[0]?.appId === undefined) {
                handleSaveApplication(false);
            } else {
                handleSaveApplication(true);
            }
        } else {
            if (selectedRows[0]?.appId !== undefined) {
                handleSaveApplication(true);
            } else {
                handleSaveApplication(false);
            }
        }
    };

    useEffect(() => {
        if (editedData?.appId) {
            setTitle(t("Modify an application"));
            setDesignation(
                editedData[t("Application")] ?? editedData["Application"]
            );
            setRemark(editedData[t("Note")]);
            setIp(editedData[t("Address IP")]);
            setCodeCaisse(
                editedData.codeCaisse !== undefined
                    ? String(editedData.codeCaisse)
                    : ""
            );
        } else {
            setTitle(t("Add an application"));
            setDesignation("");
            setRemark("");
            setIp("");
            setCodeCaisse("");
        }
        setInputDesignation(false);
        setIsApplicationListExist(false);
        setIsIpExist(false);
        setIsCodeCaisseRequired(false);
        setIsCodeCaisseDuplicate(false);
        setIsValidateButtonClicked(true);
        // eslint-disable-next-line
    }, [selectedRows, t, isAddNewApplicationButtonClicked, isAddApplication]);

    React.useEffect(() => {
        if (isPlusIconClicked) {
            setInputDesignation(false);
            setIsApplicationListExist(false);
        }
    }, [isPlusIconClicked]);

    const handleClose = () => {
        if (isEdited === true) {
            setIsActionsClicked(!isActionsClicked);
            setActions(() => {
                setIsEdited(false);
                setIsAddApplication!(!isAddApplication);
                setIsValidateButtonClicked(true);
            });
        } else {
            setIsAddApplication!(!isAddApplication);
            setIsValidateButtonClicked(true);
        }
        setEditedData({});
    };

    useEffect(() => {
        if (Object.values(selectedRows).length !== 0) {
            if (selectedRows[t("Application")] !== "") {
                setInputDesignation(false);
            }
        }
        setIsValidateButtonClicked(true);
        // eslint-disable-next-line
    }, [selectedRows]);

    return (
        <React.Fragment>
            <AvForm className="card">
                <ApplicationHeader title={title} onClose={handleClose} />
                <ApplicationForm
                    designation={designation}
                    setDesignation={setDesignation}
                    inputDesignation={inputDesignation}
                    setInputDesignation={setInputDesignation}
                    isApplicationListExist={isApplicationListExist}
                    setIsApplicationListExist={setIsApplicationListExist}
                    localNamesApplications={localNamesApplications}
                    setIsEdited={setIsEdited}
                    setIsValidateButtonClicked={setIsValidateButtonClicked}
                    ip={ip}
                    setIp={setIp}
                    isIpExist={isIpExist}
                    setIsIpExist={setIsIpExist}
                    localIps={localIps}
                    remark={remark}
                    setRemark={setRemark}
                    matchWordDesignation={matchWordDesignation}
                    codeCaisse={codeCaisse}
                    setCodeCaisse={setCodeCaisse}
                    isCodeCaisseRequired={isCodeCaisseRequired}
                    setIsCodeCaisseRequired={setIsCodeCaisseRequired}
                    isCodeCaisseDuplicate={isCodeCaisseDuplicate}
                    setIsCodeCaisseDuplicate={setIsCodeCaisseDuplicate}
                    localCodesCaisse={localCodesCaisse}
                />
                <ApplicationFooter
                    onCancel={handleClose}
                    onValidate={handleValidate}
                    isValidateButtonClicked={isValidateButtonClicked}
                />
            </AvForm>
            <ConfirmationEditMessage
                isModalOpened={isModalOpened}
                setIsModalOpened={setIsModalOpened}
                setIsAddClicked={setIsAddApplication}
                setEditedData={setSelectedRow}
            />
        </React.Fragment>
    );
}
